import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ResizeIcon from "assets/icons/icon-resize.svg";
import { TextNote1, textStylePlaceholder } from "..";
import { inputBorderStyle, InputWrapper } from "../Wrapper/InputBase";
import { colors } from "common/const/colors";
import { useValidCheckSingle } from "common/hooks/validCheck";
import { convertToPx } from "common/const/const";

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledTextArea = styled.textarea`
  ${inputBorderStyle}

  ${textStylePlaceholder}
  font-family: "Pretendard Variable";
  line-height: 160%;
  min-height: ${({ $minHeight }) => $minHeight};
  flex-grow: ${({ $height }) => $height === "100%" && 1};
  height: ${({ $height }) => `${$height}px`};
  max-height: ${({ $maxHeight }) => $maxHeight};
  padding: 1.25rem;
  resize: none;

  scrollbar-width: none;
  box-sizing: border-box;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0.625rem;
  right: 0.625rem;
  width: 1.25rem;
  height: 1.25rem;
  background: url(${ResizeIcon}) no-repeat center;
  background-size: contain;
  cursor: nwse-resize;
  z-index: 1;
  box-sizing: border-box;
`;

export const TextArea = ({
  //InputWrapper
  label,
  rules,

  //TextArea
  name,
  defaultValue,
  fixedHeight,
  minHeight,
  maxHeight,
  placeholder,
  disabled,
  readOnly,
  showCount,
}) => {
  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name: name,
    rules: rules,
    defaultValue: defaultValue,
  });

  const textAreaRef = useRef(null);
  const [height, setHeight] = useState(
    convertToPx(fixedHeight || minHeight) || 0
  );
  const isResizing = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);

  const preventClick = (clickEvent) => {
    clickEvent.stopPropagation();
    clickEvent.preventDefault();
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();

    isResizing.current = true;
    startY.current = e.clientY;
    startHeight.current = textAreaRef.current.offsetHeight || 0;

    window.addEventListener("click", preventClick, {
      once: true,
      capture: true,
    });
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
  };

  const handleResize = (e) => {
    if (!isResizing.current) return;

    const finalMinHeight = convertToPx(minHeight || 0);
    const finalMaxHeight = convertToPx(maxHeight || Infinity);

    const deltaY = e.clientY - startY.current;
    const newHeight = Math.min(
      finalMaxHeight,
      Math.max(finalMinHeight, height + deltaY)
    );

    setHeight(newHeight);
  };

  const stopResize = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleResize);
  };

  const mergedRef = (el) => {
    textAreaRef.current = el;
    inputProps.ref?.(el);
  };

  return (
    <InputWrapper
      style={{ height: "100%" }}
      label={label}
      caption={
        showCount && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <TextNote1
              color={colors.LightNavy}
              style={{ justifySelf: "flex-end" }}>
              {(inputProps.value ?? "").length}/{rules.maxLength.value}
            </TextNote1>
          </div>
        )
      }
      rules={rules}
      hasError={!!error}>
      <TextAreaWrapper>
        <StyledTextArea
          {...inputProps}
          name={name}
          ref={mergedRef}
          value={disabled ? "" : readOnly ? placeholder : inputProps.value}
          placeholder={disabled ? inputProps.value : placeholder}
          onChange={onValueChange}
          disabled={disabled}
          readOnly={readOnly}
          $error={!!error}
          $height={height}
          $minHeight={minHeight}
          $maxHeight={maxHeight}
          maxLength={rules?.maxLength?.value ?? Infinity}
        />
        {(!fixedHeight || minHeight || maxHeight) && (
          <ResizeHandle onMouseDown={startResize} />
        )}
      </TextAreaWrapper>
    </InputWrapper>
  );
};
